import React from 'react';
import { cn } from '@/lib/utils';

interface SimpleMarkdownMessageProps {
  content: string;
  className?: string;
  variant?: 'user' | 'coach';
}

export function SimpleMarkdownMessage({ content, className, variant = 'coach' }: SimpleMarkdownMessageProps) {
  const isUser = variant === 'user';
  
  // Helper function to process text for markdown
  const processTextForMarkdown = (text: string): React.ReactNode => {
    // Handle bold text first
    let processed = text.split(/\*\*(.+?)\*\*/g).map((part, i) => 
      i % 2 === 1 ? (
        <strong key={`bold-${i}`} className={cn(
          "font-semibold",
          isUser ? "text-white" : "text-gray-900"
        )}>{part}</strong>
      ) : part
    );
    
    // Handle italic text
    processed = processed.map((item, idx) => {
      if (typeof item === 'string') {
        return item.split(/\*(.+?)\*/g).map((part, i) => 
          i % 2 === 1 ? (
            <em key={`italic-${idx}-${i}`} className={cn(
              "italic",
              isUser ? "text-white/90" : "text-gray-800"
            )}>{part}</em>
          ) : part
        );
      }
      return item;
    }).flat();
    
    return processed;
  };

  // Process the content to handle markdown manually
  const processContent = (text: string) => {
    // Split into lines
    const lines = text.split('\n').filter(line => line.trim());
    
    return lines.map((line, index) => {
      // Check if it's a list item first
      const numberedListMatch = line.match(/^(\d+)\.\s+(.+)$/);
      const bulletListMatch = line.match(/^[-*]\s+(.+)$/);
      
      if (numberedListMatch) {
        const number = numberedListMatch[1];
        const content = numberedListMatch[2];
        
        return (
          <div key={index} className="flex gap-2">
            <span className={cn(
              "font-medium",
              isUser ? "text-white" : "text-gray-700"
            )}>{number}.</span>
            <span className={cn(
              isUser ? "text-white/95" : "text-gray-700"
            )}>{processTextForMarkdown(content)}</span>
          </div>
        );
      } else if (bulletListMatch) {
        const content = bulletListMatch[1];
        
        return (
          <div key={index} className="flex gap-2">
            <span className={cn(
              isUser ? "text-white" : "text-gray-700"
            )}>•</span>
            <span className={cn(
              isUser ? "text-white/95" : "text-gray-700"
            )}>{processTextForMarkdown(content)}</span>
          </div>
        );
      } else {
        return (
          <div key={index} className={cn(
            isUser ? "text-white/95" : "text-gray-800"
          )}>
            {processTextForMarkdown(line)}
          </div>
        );
      }
    });
  };
  
  return (
    <div className={cn('space-y-1', className)}>
      {processContent(content)}
    </div>
  );
}